import React, { useEffect, useRef, useState } from 'react';
import { Loader2, CheckCircle2, XCircle, ExternalLink } from 'lucide-react';
import { Dialog } from '../ui/dialog';
import { Button } from '../ui/button';
import config from '../../config';
import { getAuthHeader } from '../../utils/axiosInstance';

const STAGES = [
  ['planning', 'Planning'],
  ['loading', 'Loading data'],
  ['generating', 'Generating'],
  ['quality', 'Quality check'],
  ['uploading', 'Publishing to bucket'],
];
const stageIndex = (s) => Math.max(0, STAGES.findIndex(([k]) => k === s));

/**
 * Streams `GET /api/feeds/{id}/generate_stream/` (SSE) and shows live progress.
 * The stream is (re)started whenever `trigger` changes — it keeps running even
 * if the dialog is hidden, so closing won't cancel an in-flight generation.
 */
export default function GenerateModal({ feedId, feedName, trigger, open, onClose, onStart, onDone }) {
  const [stage, setStage] = useState('planning');
  const [prog, setProg] = useState({ processed: 0, total: 0, out: 0 });
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!trigger) return undefined;
    setStage('planning'); setProg({ processed: 0, total: 0, out: 0 }); setResult(null); setError(null);
    onStart?.();
    const ac = new AbortController();
    abortRef.current = ac;
    (async () => {
      try {
        const headers = await getAuthHeader();
        const base = (config.apiUrl || '').replace(/\/$/, '');
        const res = await fetch(`${base}/api/feeds/${feedId}/generate_stream/`, { headers, signal: ac.signal });
        if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = '';
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          let i;
          while ((i = buf.indexOf('\n\n')) >= 0) {
            const block = buf.slice(0, i); buf = buf.slice(i + 2);
            const line = block.split('\n').find((l) => l.startsWith('data:'));
            if (!line) continue;
            let ev; try { ev = JSON.parse(line.slice(5).trim()); } catch { continue; }
            if (ev.stage) setStage(ev.stage);
            if (ev.stage === 'generating' || ev.stage === 'quality') {
              setProg({ processed: ev.processed ?? 0, total: ev.total ?? 0, out: ev.out ?? 0 });
            }
            if (ev.stage === 'done') { setResult(ev.result); onDone?.(ev.result); }
            if (ev.stage === 'error') { setError(ev.error || 'Generation failed'); onDone?.(null); }
          }
        }
      } catch (e) {
        if (!ac.signal.aborted) { setError(String(e.message || e)); onDone?.(null); }
      }
    })();
    return () => ac.abort();
  }, [trigger]); // eslint-disable-line react-hooks/exhaustive-deps

  const pct = result ? 100 : (prog.total ? Math.min(100, Math.round((prog.processed / prog.total) * 100)) : 0);
  const finished = !!(result || error);

  return (
    <Dialog open={open} onClose={onClose} className="max-w-lg">
      <div className="space-y-5">
        <div>
          <h2 className="text-lg font-semibold">
            {result ? 'Feed generated' : error ? 'Generation failed' : 'Generating feed'}
          </h2>
          <p className="text-sm text-muted-foreground">{feedName}</p>
        </div>

        {!finished && (
          <>
            <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
              {STAGES.map(([k, label], idx) => {
                const cur = stageIndex(stage);
                const state = idx < cur ? 'done' : idx === cur ? 'active' : 'todo';
                return (
                  <span key={k} className={
                    state === 'active' ? 'rounded bg-primary/10 px-2 py-0.5 font-medium text-primary'
                      : state === 'done' ? 'rounded bg-emerald-500/10 px-2 py-0.5 text-emerald-600'
                        : 'rounded px-2 py-0.5 text-muted-foreground'
                  }>{label}</span>
                );
              })}
            </div>
            <div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                {prog.total > 0
                  ? <div className="h-full rounded-full bg-primary transition-all duration-300" style={{ width: `${pct}%` }} />
                  : <div className="h-full w-1/4 rounded-full bg-primary animate-indeterminate" />}
              </div>
              <div className="mt-1.5 flex items-center justify-between text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {(STAGES.find(([k]) => k === stage) || [, stage])[1]}…</span>
                {prog.total > 0 && <span className="tabular-nums">{prog.processed.toLocaleString()} / {prog.total.toLocaleString()} rows · {prog.out.toLocaleString()} out</span>}
              </div>
            </div>
          </>
        )}

        {result && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-emerald-600">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-medium">Success — {result.quality?.score}% quality{result.duration != null ? ` · ${result.duration.toFixed(1)}s` : ''}</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              {[['in', result.counts.items_in], ['excluded', result.counts.items_excluded], ['out', result.counts.items_out]].map(([label, value]) => (
                <div key={label} className="rounded-md border border-border py-2">
                  <div className="text-lg font-semibold tabular-nums">{(value ?? 0).toLocaleString()}</div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            <XCircle className="mt-0.5 h-4 w-4 shrink-0" /> <span className="break-words">{error}</span>
          </div>
        )}

        <div className="flex justify-end gap-2">
          {result?.gcs_url && (
            <a href={result.gcs_url} target="_blank" rel="noreferrer">
              <Button variant="outline" className="gap-1.5 border-emerald-600/30 text-emerald-700"><ExternalLink className="h-4 w-4" /> Public URL</Button>
            </a>
          )}
          {result?.output_url && (
            <a href={result.output_url} target="_blank" rel="noreferrer">
              <Button variant="outline" className="gap-1.5"><ExternalLink className="h-4 w-4" /> View feed</Button>
            </a>
          )}
          <Button variant={finished ? 'default' : 'secondary'} onClick={onClose}>
            {finished ? 'Done' : 'Hide (keeps running)'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
